import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User, Settings, Bell, Shield, Save } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const Profile = () => {
  const { user, updatePreferences } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState(user?.preferences || {});

  const handlePreferenceChange = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleNotificationChange = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [key]: value
      }
    }));
  };

  const handleSavePreferences = async () => {
    setLoading(true);
    const result = await updatePreferences(preferences);
    setLoading(false);
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'preferences', label: 'Trading Preferences', icon: Settings },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield }
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Profile Settings</h1>
        <p className="text-gray-600 mt-2">
          Manage your account settings and trading preferences
        </p>
      </div>

      <div className="grid lg:grid-cols-4 gap-8">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <nav className="space-y-1">
            {tabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                  activeTab === id
                    ? 'bg-primary-50 text-primary-600 border-r-2 border-primary-600'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <div className="card">
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-gray-900">Profile Information</h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={user?.firstName || ''}
                      className="input"
                      disabled
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={user?.lastName || ''}
                      className="input"
                      disabled
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={user?.email || ''}
                      className="input"
                      disabled
                    />
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Account Statistics</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-gray-900">
                        {user?.preferences?.watchlist?.length || 0}
                      </div>
                      <div className="text-sm text-gray-600">Watchlist Items</div>
                    </div>
                    
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-gray-900">
                        {user?.createdAt ? Math.floor((Date.now() - new Date(user.createdAt).getTime()) / (1000 * 60 * 60 * 24)) : 0}
                      </div>
                      <div className="text-sm text-gray-600">Days Active</div>
                    </div>
                    
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-gray-900">
                        {user?.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                      </div>
                      <div className="text-sm text-gray-600">Last Login</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'preferences' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-gray-900">Trading Preferences</h2>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Risk Tolerance
                    </label>
                    <select
                      value={preferences.riskTolerance || 'moderate'}
                      onChange={(e) => handlePreferenceChange('riskTolerance', e.target.value)}
                      className="input"
                    >
                      <option value="conservative">Conservative - Lower risk, stable returns</option>
                      <option value="moderate">Moderate - Balanced risk and return</option>
                      <option value="aggressive">Aggressive - Higher risk, higher potential returns</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Market Cap Preference
                    </label>
                    <select
                      value={preferences.marketCapPreference || 'all'}
                      onChange={(e) => handlePreferenceChange('marketCapPreference', e.target.value)}
                      className="input"
                    >
                      <option value="all">All Market Caps</option>
                      <option value="large">Large Cap (&gt;$10B)</option>
                      <option value="mid">Mid Cap ($2B-$10B)</option>
                      <option value="small">Small Cap (&lt;$2B)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Trading Style
                    </label>
                    <select
                      value={preferences.tradingStyle || 'all'}
                      onChange={(e) => handlePreferenceChange('tradingStyle', e.target.value)}
                      className="input"
                    >
                      <option value="all">All Styles</option>
                      <option value="scalping">Scalping - Quick, small profits</option>
                      <option value="momentum">Momentum - Follow trending stocks</option>
                      <option value="swing">Swing - Hold for days/weeks</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preferred Sectors
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                      {['Technology', 'Healthcare', 'Finance', 'Energy', 'Consumer', 'Industrial'].map(sector => (
                        <label key={sector} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={preferences.sectors?.includes(sector) || false}
                            onChange={(e) => {
                              const sectors = preferences.sectors || [];
                              if (e.target.checked) {
                                handlePreferenceChange('sectors', [...sectors, sector]);
                              } else {
                                handlePreferenceChange('sectors', sectors.filter(s => s !== sector));
                              }
                            }}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-700">{sector}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={handleSavePreferences}
                    disabled={loading}
                    className="btn-primary flex items-center space-x-2"
                  >
                    {loading ? <LoadingSpinner size="sm" /> : <Save className="h-4 w-4" />}
                    <span>{loading ? 'Saving...' : 'Save Preferences'}</span>
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-gray-900">Notification Settings</h2>
                
                <div className="space-y-4">
                  {[
                    { key: 'priceAlerts', label: 'Price Alerts', description: 'Get notified when stocks hit target prices' },
                    { key: 'newsUpdates', label: 'News Updates', description: 'Receive relevant news for your watchlist' },
                    { key: 'riskWarnings', label: 'Risk Warnings', description: 'Important risk alerts and market warnings' },
                    { key: 'dailyRecommendations', label: 'Daily Recommendations', description: 'Daily top 5 stock recommendations' }
                  ].map(({ key, label, description }) => (
                    <div key={key} className="flex items-center justify-between py-3 border-b border-gray-200 last:border-b-0">
                      <div>
                        <h3 className="font-medium text-gray-900">{label}</h3>
                        <p className="text-sm text-gray-600">{description}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preferences.notifications?.[key] ?? true}
                          onChange={(e) => handleNotificationChange(key, e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                      </label>
                    </div>
                  ))}
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={handleSavePreferences}
                    disabled={loading}
                    className="btn-primary flex items-center space-x-2"
                  >
                    {loading ? <LoadingSpinner size="sm" /> : <Save className="h-4 w-4" />}
                    <span>{loading ? 'Saving...' : 'Save Preferences'}</span>
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'security' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-gray-900">Security Settings</h2>
                
                <div className="space-y-6">
                  <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <Shield className="h-5 w-5 text-warning-600 mt-0.5" />
                      <div>
                        <h3 className="text-sm font-medium text-warning-800">
                          Security Notice
                        </h3>
                        <p className="text-sm text-warning-700 mt-1">
                          Password changes and two-factor authentication setup would be available in the full version of this application.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3 border-b border-gray-200">
                      <div>
                        <h3 className="font-medium text-gray-900">Change Password</h3>
                        <p className="text-sm text-gray-600">Update your account password</p>
                      </div>
                      <button className="btn-secondary" disabled>
                        Change Password
                      </button>
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-gray-200">
                      <div>
                        <h3 className="font-medium text-gray-900">Two-Factor Authentication</h3>
                        <p className="text-sm text-gray-600">Add an extra layer of security</p>
                      </div>
                      <button className="btn-secondary" disabled>
                        Enable 2FA
                      </button>
                    </div>

                    <div className="flex items-center justify-between py-3">
                      <div>
                        <h3 className="font-medium text-gray-900">Login Sessions</h3>
                        <p className="text-sm text-gray-600">Manage your active sessions</p>
                      </div>
                      <button className="btn-secondary" disabled>
                        View Sessions
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;